import { X, Send } from "lucide-react";
import { useState } from "react";
import Button from "./Button";
import api from "../../config/axiosInstance";
import { useTranscriptionStore } from "../../store";

const TranscriptionCreateModel = ({
    onClose,
    setShowTranscriptionCreateModel,
    currentSession
}) => {
    const [transcriptionText, setTranscriptionText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const { setCurrentSession } = useTranscriptionStore();

    const handleCreateTranscription = async () => {
        if (!transcriptionText.trim()) {
            setError("Transcription text is required");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            const response = await api.post('/api/v1/sessions/transcription', {
                transcription: transcriptionText.trim(),
                session_id: currentSession?._id
            });

            if (response.data?.status) {
                setShowTranscriptionCreateModel(false);
                setCurrentSession(currentSession?._id)
                if (onClose) {
                    onClose(response.data);
                }
            } else {
                setError(response.data?.message || "Failed to create transcription. Please try again.");
            }


        } catch (err) {
            console.error("Failed to create transcription", err);
            setError(err.response?.data?.message || "Failed to create transcription. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="border border-white/20 rounded-xl shadow-2xl max-w-2xl w-full mx-4 bg-black/70 max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-white/20">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-white">Create Transcription</h2>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowTranscriptionCreateModel(false)}
                            className="text-white/70 hover:text-white"
                        >
                            <X size={18} />
                        </Button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
                            {error}
                        </div>
                    )}

                    <textarea
                        value={transcriptionText}
                        onChange={(e) => setTranscriptionText(e.target.value)}
                        rows={12}
                        className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400 transition-all resize-none"
                        placeholder="Enter your transcription here..."
                        disabled={isLoading}
                        autoFocus
                    />
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-white/20 flex items-center justify-end gap-3">
                    <Button
                        variant="ghost"
                        onClick={() => setShowTranscriptionCreateModel(false)}
                        disabled={isLoading}
                        className="text-white/70 hover:text-white"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateTranscription}
                        disabled={!transcriptionText.trim() || isLoading}
                        className="bg-purple-600 hover:bg-purple-700 text-white transition-colors px-6 flex items-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <Send size={16} />
                                <span>Save Transcription</span>
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TranscriptionCreateModel;